import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { useDateTimeFormatPreferences } from '@/datetime/useDateTimeFormatPreferences.hook';
import { ITEM_TYPES, JOURNAL_OR_REPORT, UNNAMED_REPORT } from '@/homescreen/homescreen.constants';
import { useHomeScreenSearchText } from '@/homescreen/homescreen.store';
import { isNewHomeScreenWithMeilisearchEnabled } from '@/services/systemConfiguration.utilities';
import {
  formatJournalReportContent,
  getItemIcon,
  getWorkbook,
  translateBreadcrumb,
  translateMeilisearchBreadcrumb,
} from '@/utilities/homescreen.utilities';
import { getLockingChannel } from '@/utilities/lock.constants';
import { subscribe } from '@/utilities/socket.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, ProgressCircle } from '@seeqdev/qomponents';
import type { IconProps } from '@seeqdev/qomponents';

type Breadcrumb = {
  id: string;
  name: string;
  translationKey?: string;
};

interface NameCellProps {
  item: any;
  showBreadcrumbs: boolean;
  extraClassNames?: string;
  xstyle?: stylex.StyleXStyles;
  isCurrentWorkbook?: boolean;
}

export const NameCell = ({
  item,
  showBreadcrumbs,
  extraClassNames,
  xstyle,
  isCurrentWorkbook = false,
}: NameCellProps) => {
  const { t } = useTranslation();

  const [journalReportName, setJournalReportName] = useState<string>();
  const [journalReportContent, setJournalReportContent] = useState<string>();
  const isJournalReport = _.includes(JOURNAL_OR_REPORT, item.type);
  const searchText = useHomeScreenSearchText();
  const isAllowedLanguageCharacter = (word: string): boolean => /^[^-_/.,')'\\p{Alnum} ]+/.test(word);
  const isFoundInSearchText = (word: string): boolean => {
    if (!searchText) {
      return false;
    }

    const needle = _.toLower(searchText);
    const haystack = _.toLower(word);
    return (_.includes(needle, haystack) && isAllowedLanguageCharacter(word)) || _.includes(haystack, needle);
  };

  useEffect(() => {
    if (isJournalReport) {
      getWorkbook(item.workbookId).then((workbook) => {
        const itemWorksheet = _.find(workbook?.worksheets, { worksheetId: item.worksheetId });
        if (itemWorksheet) {
          setJournalReportName(`${workbook.name} - ${itemWorksheet.name}`);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (item.name !== t('UNNAMED') && item.name !== UNNAMED_REPORT && isJournalReport) {
      formatJournalReportContent(searchText, item.id).then((content) => setJournalReportContent(content));
    }
  }, [searchText, item]);

  const renderBreadcrumbs = (crumbs: string[] | Breadcrumb[] = []) => (
    <span data-testid="itemBreadcrumb" className={stylex.props(homeStyles.metadata).className}>
      {isNewHomeScreenWithMeilisearchEnabled()
        ? (crumbs as string[]).map((crumb) => translateMeilisearchBreadcrumb(crumb)).join(' » ')
        : (crumbs as Breadcrumb[]).map((crumb) => translateBreadcrumb(crumb)).join(' » ')}
    </span>
  );

  const renderJournalReportSearchResults = (content: string) => (
    <span className={stylex.props(homeStyles.preview).className}>
      {_.map(_.split(content, /[, ]+/), (word, index) => {
        return (
          <span
            key={`${word}-${index}`}
            className={stylex.props(isFoundInSearchText(word) && homeStyles.searchMatch).className}>
            <ContainerWithHTML content={`${word} `} />
          </span>
        );
      })}
    </span>
  );

  const [localLocked, setLocalLocked] = useState(false);
  useEffect(() => {
    let unsubscribe: () => void | undefined;
    if (item.type === SeeqNames.Types.Analysis && !item.locked) {
      unsubscribe = subscribe({
        channelId: getLockingChannel(item.id),
        onMessage: () => {
          setLocalLocked(true);
        },
      });
    }
    return () => {
      unsubscribe?.();
    };
  }, []);

  const getCrumbs = () => {
    if (isNewHomeScreenWithMeilisearchEnabled()) {
      return Array.isArray(item.breadcrumbPath) ? item.breadcrumbPath : [];
    }

    return Array.isArray(item.ancestors) ? item.ancestors : [];
  };

  return (
    <div
      {...stylex.props(homeStyles.nameCell, xstyle)}
      className={clsx(extraClassNames, stylex.props(homeStyles.nameCell, xstyle).className)}>
      {item.type === ITEM_TYPES.CAPTURE ? (
        <CaptureName name={item.name} captureNumber={item.captureNumber} workbookName={item.workbookName} />
      ) : item.type === ITEM_TYPES.CONTEXT_COMMENT ? (
        <>
          <span data-testid="itemName" className={stylex.props(homeStyles.metadata).className}>
            {item.name}
          </span>
          <span data-testid="itemDescription" {...stylex.props(homeStyles.description)}>
            {item.description}
          </span>
        </>
      ) : (
        <>
          {showBreadcrumbs && renderBreadcrumbs(getCrumbs())}
          <span data-testid="itemName" className={stylex.props(homeStyles.nameRow).className}>
            <span className={stylex.props(homeStyles.nameText).className}>{journalReportName ?? item.name}</span>
            {journalReportName == null && (item.locked || localLocked) ? (
              <Icon icon={faLock} xstyle={[homeStyles.lockIcon]} testId="lockedAnalysis" />
            ) : null}
          </span>
          {isJournalReport && !_.isNil(journalReportContent) && (
            <span data-testid="journalReportPreview">{renderJournalReportSearchResults(journalReportContent)}</span>
          )}
        </>
      )}
      {isCurrentWorkbook && (
        <span className={stylex.props(homeStyles.metadata).className}>({t('EXPLORER_MODAL.CURRENT_WORKBOOK')})</span>
      )}
    </div>
  );
};

type CaptureNameProps = {
  name: string;
  captureNumber?: string;
  workbookName?: string;
};

/** The name cell of a capture search result: its title over its number and source workbook. */
const CaptureName = ({ name, captureNumber, workbookName }: CaptureNameProps) => (
  <>
    <span data-testid="itemName">{name}</span>
    <span data-testid="captureSource" className={stylex.props(homeStyles.captureSource).className}>
      {[captureNumber, workbookName].filter(Boolean).join(' · ')}
    </span>
  </>
);

interface HomeScreenIconCellProps {
  item: any;
  spinning?: boolean;
}

export const HomeScreenItemIconCell = ({ item, spinning = false }: HomeScreenIconCellProps) => {
  const itemIcon = getItemIcon(item);

  return (
    <div data-testid={`icon_${item.id}`}>
      <span
        className={clsx(
          item.type !== ITEM_TYPES.CONTEXT_COMMENT && 'workbenchDisplay',
          item.type === ITEM_TYPES.JOURNAL && 'workbenchAnalysis',
          item.type === ITEM_TYPES.REPORT && 'workbenchReport',
          item.type === ITEM_TYPES.ACTION_BOARD && 'actionBoard',
          item.type === ITEM_TYPES.CONTEXT_COMMENT && 'sq-text-vantage',
          stylex.props(homeStyles.itemIcon).className,
        )}>
        <IconWithSpinner
          spinning={spinning}
          icon={itemIcon}
          type="inherit"
          extraClassNames={clsx(
            !(item.type === ITEM_TYPES.FOLDER && !spinning) && undefined,
            stylex.props(item.type === ITEM_TYPES.FOLDER && !spinning && homeStyles.folderIcon).className,
          )}
        />
      </span>
    </div>
  );
};

interface DateCellProps {
  item: any;
  accessor: string;
}

export const DateCell = ({ item, accessor }: DateCellProps) => {
  const { use24HourFormat } = useDateTimeFormatPreferences();
  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);

  if (!item[accessor]) {
    return null;
  }

  const format = getPreferredTimeFormat(use24HourFormat);
  return <span>{formatTime(item[accessor], timezone, format)}</span>;
};

export const ProgressCell = ({ percentage }: { percentage: number }) => (
  <div className={stylex.props(homeStyles.centeredCell).className}>
    <span className={stylex.props(homeStyles.progressIcon).className}>
      <ProgressCircle value={percentage} size="sm" />
    </span>
  </div>
);

export const IconCell = (props: IconProps) => (
  <div className={stylex.props(homeStyles.centeredCell).className}>
    <Icon {...props} />
  </div>
);

const homeStyles = stylex.create({
  description: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400, display: 'block' },
  metadata: {
    fontSize: 'var(--font-size-xsm)',
  },
  preview: {
    fontSize: '92%',
  },
  searchMatch: {
    fontWeight: 700,
  },
  nameCell: {
    display: 'flex',
    minWidth: '0rem',
    flexDirection: 'column',
  },
  nameRow: {
    display: 'flex',
    minWidth: '0rem',
  },
  nameText: {
    minWidth: '0rem',
    overflowWrap: 'anywhere',
  },
  lockIcon: {
    marginTop: '0.1875rem',
    marginRight: '0.3125rem',
    marginBottom: '0.3125rem',
    marginLeft: '0.3125rem',
    flexShrink: 0,
  },
  captureSource: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  itemIcon: {
    display: 'flex',
    height: '26px',
    width: '26px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.3125rem',
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  folderIcon: {
    position: 'relative',
    left: '1px',
  },
  centeredCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressIcon: {
    position: 'relative',
    display: 'inline-block',
    width: '1.1875rem',
    height: '1.25rem',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
});
